import { AUDIO_PRECACHE_NAME, VIDEO_PRECACHE_NAME, IMAGES_PRECACHE_NAME } from '../../config/precache';

// One HEAD per url: Content-Length drives the byte progress bar, and together
// with Last-Modified it identifies the cached copy of a media (ETag is not
// CORS-exposed by Supabase storage; these two headers are safelisted, so they
// are always readable — both here and on the responses already in cache).
const getUrlsMeta = async (urls) => {
  const metas = await Promise.all(
    urls.map(async (url) => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        if (!res.ok) return { size: 0, lastModified: null };
        return {
          size: Number(res.headers.get('Content-Length')) || 0,
          lastModified: res.headers.get('Last-Modified'),
        };
      } catch {
        return { size: 0, lastModified: null };
      }
    })
  );
  return new Map(urls.map((url, index) => [url, metas[index]]));
};

const cacheUrlsWithProgress = async (cacheName, urls, onProgress, { parallel } = {}) => {
  const cache = await caches.open(cacheName);
  const urlsMeta = await getUrlsMeta(urls);
  const totalBytes = [...urlsMeta.values()].reduce((sum, meta) => sum + meta.size, 0);
  const useBytes = totalBytes > 0;
  const total = useBytes ? totalBytes : urls.length;
  let loaded = 0;
  let doneFiles = 0;
  onProgress?.(0, total);

  // Media are overwritten in place at a stable path (agami-cloud upserts by
  // spotId), so the URL alone never proves the content: the cached copy is
  // current iff upload timestamp AND size both match the fresh HEAD. Any
  // missing header on either side falls through to a normal re-download.
  const hasUnchangedCachedCopy = async (url) => {
    const meta = urlsMeta.get(url);
    if (!meta?.lastModified || !meta.size) return false;
    const cached = await cache.match(url, { ignoreVary: true, ignoreSearch: true });
    if (!cached) return false;
    return (
      cached.headers.get('Last-Modified') === meta.lastModified &&
      Number(cached.headers.get('Content-Length')) === meta.size
    );
  };

  const cacheOne = async (url) => {
    if (await hasUnchangedCachedCopy(url)) {
      if (useBytes) {
        loaded += urlsMeta.get(url).size;
        onProgress?.(loaded, total);
      } else {
        doneFiles += 1;
        onProgress?.(doneFiles, total);
      }
      console.info('Media unchanged, kept cached copy', url);
      return;
    }

    await cache.delete(url, { ignoreVary: true, ignoreSearch: true });
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Cannot load url ${url}`);
    }

    if (useBytes && response.body) {
      const reader = response.body.getReader();
      const counted = new ReadableStream({
        async pull(controller) {
          const { done, value } = await reader.read();
          if (done) {
            controller.close();
            return;
          }
          loaded += value.byteLength;
          onProgress?.(loaded, total);
          controller.enqueue(value);
        },
        cancel(reason) {
          reader.cancel(reason);
        },
      });
      await cache.put(
        url,
        new Response(counted, {
          headers: response.headers,
          status: response.status,
          statusText: response.statusText,
        })
      );
    } else {
      await cache.put(url, response);
      doneFiles += 1;
      if (!useBytes) onProgress?.(doneFiles, total);
    }
  };

  if (parallel) {
    const results = await Promise.allSettled(urls.map(cacheOne));
    return results.every((result) => result.status === 'fulfilled');
  }
  await urls.reduce(async (prev, url) => {
    await prev;
    await cacheOne(url);
  }, Promise.resolve());
  return true;
};

export const downloadAudio = async (experience, onProgress) => {
  const { config } = experience;
  if (!config?.audio?.sounds || config.audio.sounds.length === 0) return;

  try {
    const audioUrls = config.audio?.sounds?.map((audio) => audio.url) || [];
    const ok = await cacheUrlsWithProgress(AUDIO_PRECACHE_NAME, audioUrls, onProgress, {
      parallel: true,
    });
    if (!ok) {
      console.error('Error caching audio files');
      return { error: 'Error caching audio files' };
    }

    console.info('Audio files cached successfully');
  } catch (e) {
    console.error('Error caching audio files', e);
    return { error: 'Error caching audio files' };
  }
};

export const downloadVideo = async (experience, onProgress) => {
  const { config } = experience;
  if (!config?.video?.videos || config.video.videos.length === 0) return;

  try {
    const videoUrls = config.video?.videos?.map((video) => video.url) || [];
    await cacheUrlsWithProgress(VIDEO_PRECACHE_NAME, videoUrls, onProgress, { parallel: false });

    console.info('Video files cached successfully');
  } catch (e) {
    console.error('Error caching video files', e);
    return { error: 'Error caching video files' };
  }
};

export const downloadCredits = async (experience) => {
  if (!experience.creditsEnabled || experience.creditsBlocks?.length === 0) return;
  const credits = experience.creditsBlocks;

  try {
    const imageUrls =
      credits
        .filter((block) => block && block?.type === 'images' && block.images)
        .flatMap((block) => block.images?.map((image) => image.url)) || [];

    const cache = await caches.open(IMAGES_PRECACHE_NAME);
    const results = await Promise.allSettled(
      imageUrls.map(async (url) => {
        await cache.delete(url, {
          ignoreVary: true,
          ignoreSearch: true,
        });
        return fetch(url).then((response) => {
          if (!response.ok) {
            console.error('Cannot load image url', url);
            throw Error('Cannot load image url', url);
          }
          return cache.put(url, response);
        });
      })
    );
    if (results.find((result) => result.status !== 'fulfilled')) {
      console.error('Error caching image files');
      return { error: 'Error caching image files' };
    }

    console.info('Image files cached successfully');
  } catch (e) {
    console.error('Error caching image files', e);
    return { error: 'Error caching image files' };
  }
};
